import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react"
import { Fragment, useCallback, useState } from "react"
import { Button, IconButton } from "~/components/reusable/Button"
import { Separator } from "~/components/reusable/Separator"
import Select from "~/components/reusable/Select"
import { cn } from "~/utils/helpers"
import {
  Filter,
  filterFields as defaultFilterFields,
  getFilterOperators,
  normalizeGates,
  type FilterFieldType,
  type FilterGate,
} from "~/utils/filter"
import { FilterValueInput } from "./FilterValueInput"
import { FilterPill } from "./FilterPill"
import { GateToggle } from "./GateToggle"
import { QuickFiltersList } from "./QuickFiltersList"
import { type FilterFieldGroup, FilterFieldGroupList } from "./FilterFieldGroupList"
import { useFilterOperations } from "~/hooks/useFilterOperations"
import { useFilterFields } from "~/hooks/useFilterFields"
import { useAuth0 } from "@auth0/auth0-react"

interface FilterGroupEditorProps {
  filters: Filter[]
  onFiltersChange: (filters: Filter[]) => void
  isLocal?: boolean
  customFilterFields?: FilterFieldType[]
  customFilterFieldOptions?: { label: string; value: string | undefined }[]
  extraFilterFields?: FilterFieldType[]
  extraFilterOptions?: React.ReactNode
  /** Rows beside the quick filters, each opening its own fields. */
  fieldGroups?: FilterFieldGroup[]
  onBack?: () => void
  enableQuickFilters?: boolean
  enableGroups?: boolean
  onApply?: () => void
}

export function FilterGroupEditor({
  filters,
  onFiltersChange,
  isLocal = false,
  customFilterFields,
  customFilterFieldOptions,
  extraFilterFields,
  extraFilterOptions,
  fieldGroups,
  onBack,
  enableQuickFilters = true,
  enableGroups = true,
  onApply,
}: FilterGroupEditorProps) {
  const [showQuickFilters, setShowQuickFilters] = useState(false)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const { quickFilterFields, nonBooleanQuickFilters } = useFilterFields()
  const { user } = useAuth0()

  const handleQuickFilterClick = useCallback(
    (
      filterType: { name: string; value: string; type: string; icon?: React.ReactNode },
      isNonBoolean: boolean,
    ) => {
      let value = ""
      let values: string[] = []
      let operator = "="

      if (isNonBoolean && filterType.name === "My SKUs") {
        value = user?.email ?? ""
        operator = "include"
      } else if (isNonBoolean) {
        values = ["NOOS"]
        operator = "oneOf"
      } else {
        value = "true"
      }

      const newFilter: Filter = {
        field: filterType.value,
        operator,
        value,
        values,
        by: "variantId",
      }

      onFiltersChange(normalizeGates([...filters, newFilter]))
      setShowQuickFilters(false)
    },
    [filters, onFiltersChange, user?.email],
  )

  const {
    incompleteFilters,
    filterFieldOptions,
    filterFields,
    handleFilterArrayValuesChange,
    handleFilterSingleValueChange,
    handleFilterGroupChange,
    handleAddIncompleteFilter,
    handleAllIncompleteGatesChange,
    handleIncompleteFilterFieldChange,
    handleIncompleteFilterOperatorChange,
    handleIncompleteFilterValueChange,
    handleIncompleteFilterArrayValueChange,
    handleRemoveIncompleteFilter,
    handleCompleteFilterFieldChange,
    handleCompleteFilterOperatorChange,
    removeFilter,
    clearAllLocallyStoredFilters,
    clearAllFilters,
  } = useFilterOperations({
    filters,
    onFiltersChange,
    filterFields: customFilterFields,
    filterFieldOptions: customFilterFieldOptions,
    extraFilterFields,
  })

  const clearFilters = useCallback(() => {
    if (isLocal) {
      clearAllLocallyStoredFilters()
    } else {
      clearAllFilters()
    }
  }, [isLocal, clearAllLocallyStoredFilters, clearAllFilters])

  const existingGroupIds = Array.from(
    new Set(filters.map((f) => f.groupId).filter((g): g is number => g != null)),
  ).sort((a, b) => a - b)
  const nextGroupId = (existingGroupIds[existingGroupIds.length - 1] ?? 0) + 1
  const groupOptions: { label: string; value: number | undefined }[] = [
    { label: "No group", value: undefined },
    ...existingGroupIds.map((id) => ({ label: `Group ${id}`, value: id as number | undefined })),
    { label: "New group", value: nextGroupId },
  ]

  const groupIds = new Set(filters.map((f) => f.groupId).filter((g): g is number => g != null))
  const isOrGroup =
    filters.length > 0 && filters.every((f) => f.groupId != null) && groupIds.size === 1
  const isFlatAnd = filters.length >= 2 && filters.every((f) => f.groupId == null)
  const combineGate: FilterGate = isOrGroup
    ? "or"
    : isFlatAnd
      ? "and"
      : (incompleteFilters.find((inc) => inc.gate)?.gate ?? "and")
  const hasCompleteConnector = filters.length >= 2
  const incompleteControlIdx = hasCompleteConnector ? -1 : filters.length === 0 ? 1 : 0

  const toggleCombine = () => {
    const next: FilterGate = combineGate === "and" ? "or" : "and"
    if (filters.length >= 1) {
      onFiltersChange(
        normalizeGates(
          next === "or"
            ? filters.map((f) => ({ ...f, groupId: 1, gate: undefined }))
            : filters.map((f) => ({ ...f, groupId: undefined })),
        ),
      )
    }
    handleAllIncompleteGatesChange(next)
  }

  return (
    <div className="flex flex-col">
      {onBack ? (
        <div className="flex flex-row items-center gap-1 p-2">
          <IconButton size="sm" variant="transparent" onClick={onBack} aria-label="Back">
            <ArrowLeft size={16} />
          </IconButton>
          <p className="text-base font-medium">Advanced filters</p>
        </div>
      ) : (
        <p className="text-base font-medium p-3">Filters</p>
      )}
      <Separator orientation="horizontal" />
      {extraFilterOptions && !showQuickFilters && (
        <>
          <div className="px-3 py-2">{extraFilterOptions}</div>
          <Separator orientation="horizontal" />
        </>
      )}
      {enableQuickFilters && (
        <>
          <QuickFiltersList
            showQuickFilters={showQuickFilters}
            setShowQuickFilters={setShowQuickFilters}
            quickFilterFields={quickFilterFields}
            nonBooleanQuickFilters={nonBooleanQuickFilters}
            onQuickFilterClick={handleQuickFilterClick}
            onQuickFiltersRowClick={() => setShowQuickFilters(true)}
          />
          <Separator orientation="horizontal" />
        </>
      )}
      {fieldGroups?.map((group) => (
        <Fragment key={group.label}>
          <FilterFieldGroupList
            group={group}
            open={openGroup === group.label}
            onOpenChange={(open) => setOpenGroup(open ? group.label : null)}
            onFieldClick={(field) => {
              handleAddIncompleteFilter(combineGate, field.value)
              setOpenGroup(null)
            }}
          />
          <Separator orientation="horizontal" />
        </Fragment>
      ))}
      {filters.length === 0 && incompleteFilters.length === 0 ? (
        <>
          <div className="px-3 py-4">
            <div className="text-sm text-fg-subtle">No filters applied</div>
          </div>
          <Separator orientation="horizontal" />
        </>
      ) : (
        <>
          {filters.map((filter, index) => {
            const filterOperatorOptions = getFilterOperators(
              filter.field,
              filterFields ?? defaultFilterFields,
            ).map((op) => ({
              label: op.name,
              value: op.value,
            }))

            const selectedFilterField = filterFieldOptions.find((opt) => opt.value === filter.field)
            const selectedFilterOperator = filterOperatorOptions.find(
              (opt) => opt.value === filter.operator,
            )

            return (
              <div key={`${index}-${filter.field}`}>
                <div className={cn("px-3 py-2 text-sm flex items-center gap-2 w-full flex-row")}>
                  <div className="w-[72px] shrink-0">
                    {index === 0 ? (
                      <span className="label-base text-fg-subtle px-1">Where</span>
                    ) : index === 1 ? (
                      <GateToggle gate={combineGate} onToggle={toggleCombine} />
                    ) : (
                      <GateToggle gate={combineGate} readOnly />
                    )}
                  </div>
                  <div className="w-[150px]">
                    <Select
                      options={filterFieldOptions}
                      value={selectedFilterField ?? null}
                      onChange={(option) => {
                        if (option && option.value !== filter.field) {
                          handleCompleteFilterFieldChange(filter, option.value ?? "", index)
                        }
                      }}
                      size="sm"
                    />
                  </div>
                  <div className="w-[130px]">
                    <Select
                      options={filterOperatorOptions}
                      value={selectedFilterOperator ?? null}
                      onChange={(option) => {
                        if (option && option.value !== filter.operator) {
                          handleCompleteFilterOperatorChange(filter, option.value, index)
                        }
                      }}
                      placeholder="Select operator"
                      size="sm"
                    />
                  </div>
                  <div className="min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                    <FilterValueInput
                      filter={filter}
                      onValueChange={(value) => {
                        if (value !== filter.value) {
                          handleFilterSingleValueChange(filter, value, index)
                        }
                      }}
                      onValuesChange={(values) => {
                        const hasChanged =
                          filter.values.length !== values.length ||
                          !filter.values.every((val, idx) => val === values[idx])
                        if (hasChanged) {
                          handleFilterArrayValuesChange(filter, values, index)
                        }
                      }}
                      size="sm"
                      extraFilterFields={extraFilterFields ?? customFilterFields}
                    />
                  </div>
                  {enableGroups && (
                    <div className="w-[110px]">
                      <Select
                        options={groupOptions}
                        value={groupOptions.find((opt) => opt.value === filter.groupId) ?? null}
                        onChange={(option) => {
                          if (option && option.value !== filter.groupId) {
                            handleFilterGroupChange(filter, option.value, index)
                          }
                        }}
                        placeholder="No group"
                        size="sm"
                      />
                    </div>
                  )}

                  <IconButton
                    className="ml-auto"
                    size="sm"
                    variant="transparent"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      removeFilter(filter, index)
                    }}
                  >
                    <Trash size={16} />
                  </IconButton>
                </div>
                <Separator orientation="horizontal" />
              </div>
            )
          })}
          {/* incomplete filters */}
          {incompleteFilters.map((incompleteFilter, incompleteIndex) => {
            const filterOperatorOptions = incompleteFilter.field
              ? getFilterOperators(incompleteFilter.field, filterFields ?? defaultFilterFields).map(
                  (op) => ({
                    label: op.name,
                    value: op.value,
                  }),
                )
              : []

            const selectedFilterField = incompleteFilter.field
              ? filterFieldOptions.find((opt) => opt.value === incompleteFilter.field)
              : null
            const selectedFilterOperator = incompleteFilter.operator
              ? filterOperatorOptions.find((opt) => opt.value === incompleteFilter.operator)
              : null

            return (
              <div key={incompleteFilter.id}>
                <div
                  className={cn(
                    "px-3 py-2 text-sm flex items-center gap-2 w-full flex-row flex-wrap",
                  )}
                >
                  <div className="w-[72px] shrink-0">
                    {filters.length === 0 && incompleteIndex === 0 ? (
                      <span className="label-base text-fg-subtle px-1">Where</span>
                    ) : !hasCompleteConnector && incompleteIndex === incompleteControlIdx ? (
                      <GateToggle gate={combineGate} onToggle={toggleCombine} />
                    ) : (
                      <GateToggle gate={combineGate} readOnly />
                    )}
                  </div>
                  <div className="w-[150px]">
                    <Select
                      options={filterFieldOptions}
                      value={selectedFilterField ?? null}
                      onChange={(option) => {
                        if (option) {
                          handleIncompleteFilterFieldChange(incompleteFilter.id, option.value ?? "")
                        }
                      }}
                      placeholder="Select field"
                      size="sm"
                    />
                  </div>
                  {incompleteFilter.field && (
                    <div className="w-[130px]">
                      <Select
                        options={filterOperatorOptions}
                        value={selectedFilterOperator ?? null}
                        onChange={(option) => {
                          if (option) {
                            handleIncompleteFilterOperatorChange(incompleteFilter.id, option.value)
                          }
                        }}
                        size="sm"
                      />
                    </div>
                  )}
                  {incompleteFilter.field && incompleteFilter.operator && (
                    <div className="min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                      <FilterValueInput
                        filter={{
                          field: incompleteFilter.field,
                          operator: incompleteFilter.operator,
                          value: "",
                          values: [],
                          by: "variantId",
                        }}
                        onValueChange={(value) => {
                          if (value) {
                            handleIncompleteFilterValueChange(incompleteFilter.id, value)
                          }
                        }}
                        onValuesChange={(values) => {
                          if (values.length > 0) {
                            handleIncompleteFilterArrayValueChange(incompleteFilter.id, values)
                          }
                        }}
                        size="sm"
                        extraFilterFields={extraFilterFields ?? customFilterFields}
                      />
                    </div>
                  )}

                  <Button
                    className="ml-auto"
                    size="sm"
                    variant="transparent"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleRemoveIncompleteFilter(incompleteFilter.id)
                    }}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
                <Separator orientation="horizontal" />
              </div>
            )
          })}
        </>
      )}
      <div className="flex flex-row items-center gap-2 px-3 py-2 justify-between">
        <FilterPill
          size="sm"
          variant="neutralOutline"
          icon={<Plus />}
          onClick={(e) => {
            e.preventDefault()
            handleAddIncompleteFilter(combineGate)
          }}
        >
          Filter
        </FilterPill>
        <Button
          size="sm"
          variant="transparent"
          onClick={(e) => {
            e.preventDefault()
            clearFilters()
          }}
        >
          Clear all
        </Button>
      </div>
      {onApply && filters.length > 0 && (
        <>
          <Separator orientation="horizontal" />
          <div className="px-3 py-2">
            <Button size="sm" onClick={onApply} className="w-full">
              {filters.length > 1 ? "Apply filters" : "Apply filter"}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
